import React, { useEffect } from "react";
import axios from "axios";
import TipsManagement from "./screens/TipsManagement";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { USER_LOGIN_SUCCESS } from "./constants/adminConstant";
import { getRole } from "./actions/AdminAction";
import { canAccessRoute, canViewDashboard, getFirstAllowedRoute } from "./constants/menuConstants";
import {
  checkMaintenanceStatus,
  setupMaintenanceInterceptor,
} from "./actions/settingsActions";
import { logout, setupLoginAccessInterceptor } from "./actions/userActions";
import { checkClubAccessStatus, setupClubInactiveInterceptor } from "./actions/clubActions";
import { CLUB_ACCESS_STATUS } from "./constants/clubConstants";
import MaintenanceScreen from "./screens/MaintenanceScreen";
import ClubInactiveScreen from "./screens/ClubInactiveScreen";
import SessionExpiryWatcher from "./components/SessionExpiryWatcher";
import HangMode from "./screens/HangMode";

import Login from "./screens/Login";
import PlayerLogin from "./screens/PlayerLogin";
import UserList from "./screens/UserList";
import AddRole from "./screens/addRole";
import Customers from "./screens/Customers";
import Roles from "./screens/Roles";
import SideBar from "./components/SideBar";
import MobileNav from "./components/MobileNav";
import Topbar from "./components/layout/Topbar";
import { EmptyState } from "./ui";
import UserByID from "./screens/UserById";
import Dashboard from "./screens/Dashboard";
import RoleByID from "./screens/RoleById";
import ProtectedRoute from "./screens/ProtectedRoute";
import PlayerProtectedRoute from "./screens/PlayerProtectedRoute";
import Wallet from "./screens/wallet";
import AdminTransactions from "./screens/AdminTransaction";
import QRScannerPage from "./screens/QRScannerPage";
import QRResultPage from "./screens/QRResultPage";
import DailyCommission from "./screens/DailyCommission";
import Reports from "./screens/Reports"; // Add this import
import AffiliatedReports from "./screens/AffiliatedReports";
import Venues from "./screens/Venues";
import PlayerProfile from "./screens/PlayerProfile";
// Analytics Dashboard is intentionally disabled.
// import AnalyticsDashboard from "./screens/AnalyticsDashboard";
import ClubLedger from "./screens/ClubLedger";
import TablePlayerReport from "./screens/TablePlayerReport";
import BookSeat from "./screens/BookSeat";
import MyBookings from "./screens/MyBookings";
import Messaging from "./screens/Messaging";
import AffiliateDashboard from "./screens/AffiliateDashboard";
import AffiliateManagement from "./screens/AffiliateManagement";
import DealerManagement from "./screens/DealerManagement";
import TimeTracking from "./screens/TimeTracking";
import DealerAudit from "./screens/DealerAudit";
import AuditTrail from "./screens/AuditTrail";
import InvoiceTemplateMaster from "./screens/InvoiceTemplateMaster";
import ManualKYC from "./screens/ManualKYC";
import ClubWelcomePage from "./screens/ClubWelcomePage";
import Players from "./screens/Players";
import ManualOTP from "./screens/ManualOTP";
import PlayerCheckInList from "./screens/PlayerCheckInList";
import PlayerSettings from "./screens/PlayerSettings";
import VerificationContainer from "./screens/VerificationContainer";

function App() {
  const dispatch = useDispatch();
  const [chaosEnabled, setChaosEnabled] = React.useState(false);
  const { userInfo: adminUserInfo } = useSelector((state) => state.adminLogin || {});
  const { loadingGetRole, errorGetRole, getRoleData } = useSelector((state) => state.getRoleInfo || {});
  const adminRoleId = adminUserInfo?.role?._id || adminUserInfo?.role || null;
  const storedAdminInfo = (() => {
    try {
      return JSON.parse(localStorage.getItem("adminInfo") || "{}");
    } catch {
      return {};
    }
  })();
  const isSuperAdmin =
    adminUserInfo?.role?.isSuperAdmin === true ||
    storedAdminInfo?.role?.isSuperAdmin === true ||
    getRoleData?.isSuperAdmin === true;
  const fetchedRoleId = getRoleData?._id || null;
  const {
    maintenanceMode,
    maintenanceMessage,
    loading: maintenanceLoading,
  } = useSelector((state) => state.maintenanceStatus);
  const clubAccess = useSelector((state) => state.clubAccessStatus);
  const adminToken = adminUserInfo?.token;

  useEffect(() => {
    // Setup axios interceptor to catch 503 maintenance responses
    setupMaintenanceInterceptor(dispatch);
    const loginAccessInterceptor = setupLoginAccessInterceptor(dispatch);
    const clubInactiveInterceptor = setupClubInactiveInterceptor(dispatch);

    // Check maintenance status on app load
    dispatch(checkMaintenanceStatus());

    // Keep already-open screens in sync when a super admin changes the mode.
    const maintenancePoll = window.setInterval(() => {
      dispatch(checkMaintenanceStatus());
    }, 10000);

    // Restore admin session from localStorage on app load
    const adminInfo = localStorage.getItem("adminInfo");
    if (adminInfo) {
      try {
        const parsedAdminInfo = JSON.parse(adminInfo);
        if (parsedAdminInfo && parsedAdminInfo.token) {
          console.log("🔄 Restoring admin session from localStorage");
          dispatch({
            type: USER_LOGIN_SUCCESS,
            payload: parsedAdminInfo,
          });
        }
      } catch (error) {
        console.error("❌ Error parsing adminInfo from localStorage:", error);
        localStorage.removeItem("adminInfo");
      }
    }
    return () => {
      window.clearInterval(maintenancePoll);
      axios.interceptors.response.eject(loginAccessInterceptor);
      axios.interceptors.response.eject(clubInactiveInterceptor);
    };
  }, [dispatch]);

  // Staff: watch for their club being deactivated or reactivated while the app is open
  useEffect(() => {
    if (!adminToken || isSuperAdmin) {
      dispatch({ type: CLUB_ACCESS_STATUS, payload: { blocked: false } });
      return undefined;
    }

    dispatch(checkClubAccessStatus());
    const clubStatusPoll = window.setInterval(() => {
      dispatch(checkClubAccessStatus());
    }, 15000);

    return () => window.clearInterval(clubStatusPoll);
  }, [dispatch, adminToken, isSuperAdmin]);

  const handleClubInactiveLogout = async () => {
    await dispatch(logout());
    dispatch({ type: CLUB_ACCESS_STATUS, payload: { blocked: false } });
    window.location.replace("/login");
  };

  useEffect(() => {
    const handleNumberInputWheel = (event) => {
      const targetInput =
        event.target instanceof Element
          ? event.target.closest('input[type="number"]')
          : null;

      if (
        targetInput instanceof HTMLInputElement &&
        document.activeElement === targetInput
      ) {
        event.preventDefault();
        targetInput.blur();
      }
    };

    document.addEventListener("wheel", handleNumberInputWheel, {
      passive: false,
    });

    return () => {
      document.removeEventListener("wheel", handleNumberInputWheel, false);
    };
  }, []);

  useEffect(() => {
    const token = adminUserInfo?.token;

    if (!token || !adminRoleId || loadingGetRole || errorGetRole) {
      return;
    }

    const roleIdMatches =
      fetchedRoleId && fetchedRoleId.toString() === adminRoleId.toString();
    const needsRoleFetch = !fetchedRoleId || !roleIdMatches;

    if (needsRoleFetch) {
      dispatch(getRole());
    }
  }, [
    dispatch,
    adminUserInfo?.token,
    adminRoleId,
    fetchedRoleId,
    loadingGetRole,
    errorGetRole,
  ]);

  // Show maintenance screen if maintenance mode is enabled
  if (!maintenanceLoading && maintenanceMode && !isSuperAdmin) {
    return <MaintenanceScreen message={maintenanceMessage} />;
  }

  // Staff whose assigned clubs are all deactivated can't use the app until reactivation
  if (adminToken && !isSuperAdmin && clubAccess?.blocked) {
    return (
      <ClubInactiveScreen
        message={clubAccess.message}
        inactiveClubs={clubAccess.inactiveClubs}
        onCheckAgain={() => dispatch(checkClubAccessStatus())}
        onLogout={handleClubInactiveLogout}
      />
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ToastContainer position="bottom-right" autoClose={4000} newestOnTop closeOnClick pauseOnFocusLoss={false} />
      <SessionExpiryWatcher />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/player-login" element={<PlayerLogin />} />
        <Route path="/verification" element={<VerificationContainer />} />
        <Route path="/welcome/:clubCode" element={<ClubWelcomePage />} />
        <Route path="/hang" element={<HangMode />} />

        {/* Dealer Management - Full Screen Mode (No Sidebar) */}
        <Route
          path="/dealer-management"
          element={
            <ProtectedRoute>
              <AdminRouteGuard>
                <DealerManagement />
              </AdminRouteGuard>
            </ProtectedRoute>
          }
        />

        {/* Player Booking Routes - No Sidebar */}
        <Route
          path="/book-seat"
          element={
            <PlayerProtectedRoute>
              <BookSeat />
            </PlayerProtectedRoute>
          }
        />
        <Route
          path="/my-bookings"
          element={
            <PlayerProtectedRoute>
              <MyBookings />
            </PlayerProtectedRoute>
          }
        />
        <Route path="/dashboard" element={<DashboardRouteResolver />} />
        <Route path="/wallet" element={<WalletRouteResolver />} />
        <Route
          path="/player-settings"
          element={
            <PlayerProtectedRoute>
              <PlayerSettings />
            </PlayerProtectedRoute>
          }
        />

        {/* All Protected Routes - With Sidebar */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <DefaultContainer />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

const safeParseStorage = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
};

const getSessionSnapshot = () => {
  const adminInfo = safeParseStorage("adminInfo");
  const userInfo = safeParseStorage("userInfo");
  const playerToken =
    userInfo?.token ||
    localStorage.getItem("token") ||
    localStorage.getItem("userToken");
  const hasPlayerId = Boolean(userInfo?.id || userInfo?._id);

  return {
    hasAdminSession: Boolean(adminInfo?.token),
    hasPlayerSession: Boolean(hasPlayerId && playerToken),
  };
};

// Staff layout: sidebar on the left, top bar + page on the right; phones get MobileNav.
const AdminShellFrame = ({ children }) => {
  return (
    <div className="flex min-h-screen bg-cm-bg text-cm-text">
      <MobileNav />
      <SideBar />
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar />
        <main id="main" className="flex-1 min-w-0 px-3 pt-[72px] pb-8 sm:px-6 sm:pt-6 lg:px-8 lg:pt-8">
          <div className="mx-auto w-full max-w-[1600px] min-w-0">{children}</div>
        </main>
      </div>
    </div>
  );
};

const NoAccessNotice = () => (
  <EmptyState
    title="No pages assigned yet"
    description="Your role doesn't include any pages. Ask an administrator to give your role access."
  />
);

// Staff role: prefer the freshly fetched role, fall back to the role stored at login
const useAdminRole = () => {
  const { getRoleData } = useSelector((state) => state.getRoleInfo || {});
  const { userInfo: adminUserInfo } = useSelector((state) => state.adminLogin || {});
  const role =
    getRoleData ||
    (typeof adminUserInfo?.role === "object" ? adminUserInfo.role : null) ||
    safeParseStorage("adminInfo").role;
  return role && typeof role === "object" ? role : null;
};

// Keeps staff off pages their role cannot open, so those pages never mount
// and never fire API calls that would 400/403
const AdminRouteGuard = ({ children }) => {
  const location = useLocation();
  const { hasAdminSession } = getSessionSnapshot();
  const adminRole = useAdminRole();

  if (!hasAdminSession || !adminRole || canAccessRoute(adminRole, location.pathname)) {
    return children;
  }

  const firstAllowedRoute = getFirstAllowedRoute(adminRole);
  if (firstAllowedRoute && firstAllowedRoute !== location.pathname) {
    return <Navigate to={firstAllowedRoute} replace />;
  }
  return <NoAccessNotice />;
};

const DashboardRouteResolver = () => {
  const { hasAdminSession, hasPlayerSession } = getSessionSnapshot();
  const adminRole = useAdminRole();

  if (hasAdminSession) {
    if (adminRole && !canViewDashboard(adminRole)) {
      const firstAllowedRoute = getFirstAllowedRoute(adminRole);
      if (firstAllowedRoute && firstAllowedRoute !== "/dashboard") {
        return <Navigate to={firstAllowedRoute} replace />;
      }
      return (
        <ProtectedRoute>
          <AdminShellFrame>
            <NoAccessNotice />
          </AdminShellFrame>
        </ProtectedRoute>
      );
    }

    return (
      <ProtectedRoute>
        <AdminShellFrame>
          <Dashboard />
        </AdminShellFrame>
      </ProtectedRoute>
    );
  }

  if (hasPlayerSession) {
    return (
      <PlayerProtectedRoute>
        <Dashboard />
      </PlayerProtectedRoute>
    );
  }

  return <Navigate to="/login" replace />;
};

const WalletRouteResolver = () => {
  const { hasAdminSession, hasPlayerSession } = getSessionSnapshot();

  if (hasAdminSession) {
    return (
      <ProtectedRoute>
        <AdminShellFrame>
          <Wallet />
        </AdminShellFrame>
      </ProtectedRoute>
    );
  }

  if (hasPlayerSession) {
    return (
      <PlayerProtectedRoute>
        <Wallet />
      </PlayerProtectedRoute>
    );
  }

  return <Navigate to="/player-login" replace />;
};

const DefaultContainer = () => {
  return (
    <AdminShellFrame>
      <AdminRouteGuard>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} exact />
          <Route path="/hang" element={<HangMode />} exact />
          <Route path="/users" element={<UserList />} exact />
          <Route path="/users/:id" element={<UserByID />} exact />
          <Route path="/customers" element={<Customers />} exact />
          <Route path="/roles" element={<Roles />} exact />
          <Route path="/roles/add" element={<AddRole />} exact />
          <Route path="/roles/update/:id" element={<RoleByID />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/admin/transactions" element={<AdminTransactions />} />
          <Route path="/qr-scanner" element={<QRScannerPage />} />
          <Route path="/qr-result" element={<QRResultPage />} />
          <Route
            path="/daily-commission"
            element={<DailyCommission />}
            exact
          />{" "}
          {/* Add this route */}
          <Route path="/reports" element={<Reports />} exact />
          <Route
            path="/reports/affiliated"
            element={<AffiliatedReports />}
            exact
          />
          <Route path="/venues" element={<Venues />} exact />
          <Route path="/locations" element={<Navigate to="/venues" replace />} exact />
          <Route path="/clubs" element={<Navigate to="/venues" replace />} exact />
          <Route path="/players" element={<Players />} exact />
          <Route path="/players/:id" element={<PlayerProfile />} exact />
          {/* Analytics Dashboard is intentionally disabled. */}
          <Route path="/analytics" element={<Navigate to="/dashboard" replace />} exact />
          <Route path="/club-ledger" element={<ClubLedger />} exact />
          <Route
            path="/reports/table-player"
            element={<TablePlayerReport />}
            exact
          />
          <Route path="/messaging" element={<Messaging />} exact />
          <Route path="/affiliate" element={<AffiliateDashboard />} exact />
          <Route
            path="/affiliate-management"
            element={<AffiliateManagement />}
            exact
          />
          <Route path="/time-tracking" element={<TimeTracking />} exact />
          <Route path="/dealer-audit" element={<DealerAudit />} exact />
          <Route path="/audit-trail" element={<AuditTrail />} exact />
          <Route path="/invoice-template" element={<InvoiceTemplateMaster />} exact />
          <Route path="/manual-kyc" element={<ManualKYC />} exact />
          <Route path="/manual-otp" element={<ManualOTP />} exact />
          <Route path="/checkin-list" element={<PlayerCheckInList />} exact />
          <Route path="/tips/dealer" element={<TipsManagement />} exact />
          <Route path="/tips/service" element={<TipsManagement />} exact />
        </Routes>
      </AdminRouteGuard>
    </AdminShellFrame>
  );
};

export default App;
